/* global rwgps, Routes */
import { actions as plannerReducerActions } from '../../Planner/reducer'
import { LOAD_EXISTING_MODAL } from 'features/search/LoadExisting'
import fetchJSON from 'lib/fetchJSON'
import { trackEvent } from 'lib/trackEvent'
import { showModal } from 'lib/modalUtils'
import { getCurrentUser } from 'lib/utils'
import _ from 'lodash'

export const leftSidebarProps = ({ dispatch }) => {
  const map = Routes.activeMap
  return {
    activate: route => {
      map.setActiveRoute(route)
    },
    setVisible: (route, visible) => {
      route.drawInactive = visible
      route.redraw()
    },
    removeRoute: route => {
      map.removeRoute(route, true)
      map.snapshot()
    },
    revertToLast: (route) => {
      const { id } = route.routeData
      fetchJSON('/routes/' + id + '.json').then(data => {
        map.loadRouteForEdit(data, true)
      })
      map.removeRoute(route, true)
      map.snapshot()
    },
    saveRoute: () => {
      dispatch(
        getCurrentUser().id
          ? plannerReducerActions.setShowSave(true)
          : plannerReducerActions.setShowSignup(true)
      )
    },
    launchReviewCues: route => {
      if (!rwgps.config.privileges['advanced_planning']) {
        return rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerReviewCues' }, rwgps.carouselUpgradeProps))
      }

      dispatch && dispatch(plannerReducerActions.setShowCueReview(true))
    },
    highlight: route => {
      route.drawPreview()
    },
    unhighlight: route => {
      route.clearPreview()
    },
    startNewRoute: () => {
      if (!rwgps.config.privileges['advanced_planning']) {
        return rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerAddRoute' }, rwgps.carouselUpgradeProps))
      }
      const route = new rwgps.Route(null, map)
      map.setActiveRoute(route)
    },
    loadExistingModal: () => {
      if (!rwgps.config.privileges['advanced_planning']) {
        return rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerImportRoutes' }, rwgps.carouselUpgradeProps))
      }
      // blow selectedFeature state else the sg selectedFeature ui renders over the "Add to Planner" CTA
      dispatch && dispatch(plannerReducerActions.setSelectedFeature(null))
      showModal(LOAD_EXISTING_MODAL, loadExistingModalProps())
    }
  }
}

const getSidebarProps = ({ userPrefs, setPreference }) => {
  const callbacks = {
    setCallbacks: {
      followRoads: () => {
        trackEvent('Route Planner', 'On Click', 'Follow roads')
        Routes.activeMap.callWhenReady(function () { Routes.activeMap.clickOptions.followRoads(true) })
      },
      createPoi: () => {
        trackEvent('Route Planner', 'On Click', 'Add POI')
        if (!rwgps.config.privileges['advanced_planning']) {
          rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerAddPoi' }, rwgps.carouselUpgradeProps))
          rwgps.Sidebar.selectCommand('followRoads')
          return
        }
        Routes.activeMap.enableAddPoi()
      },
      drawLines: () => {
        trackEvent('Route Planner', 'On Click', 'Draw lines')
        Routes.activeMap.callWhenReady(function () { Routes.activeMap.clickOptions.followRoads(false) })
      },
      addCue: () => {
        trackEvent('Route Planner', 'On Click', 'Add to cuesheet')
        if (!rwgps.config.privileges['advanced_planning']) {
          rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerAddToCuesheet' }, rwgps.carouselUpgradeProps))
          rwgps.Sidebar.selectCommand('followRoads')
          return
        }
        if (rwgps.planner.plannerOpts.tripId) {
          rwgps.Events.trigger('trip:modification_prevented')
        } else {
          Routes.activeMap.enableAddCoursePoint()
        }
      },
      createControlPoint: () => {
        trackEvent('Route Planner', 'On Click', 'Add/remove control point')
        if (rwgps.planner.plannerOpts.tripId) {
          rwgps.Events.trigger('trip:modification_prevented')
        } else {
          Routes.activeMap.enableAddControlPoint()
        }
      }
    },
    unsetCallbacks: {
      createPoi: () => {
        Routes.activeMap.disableAddPoi()
      },
      addCue: () => {
        Routes.activeMap.disableAddCoursePoint()
      },
      createControlPoint: () => {
        Routes.activeMap.disableAddControlPoint()
      }
    },
    optimizeForCallback: directionsType => {
      trackEvent('Route Planner', 'Optimize path for', directionsType[0].toUpperCase() + directionsType.substring(1))
      Routes.activeMap.callWhenReady(() => { Routes.activeMap.setRoutingMode(directionsType) })
      setPreference('route_planner_directions_type', directionsType, true)
    },
    optimizeForDefault: userPrefs.route_planner_directions_type,
    avoidHighwaysChanged: (optionName, optionValue) => {
      Routes.activeMap.callWhenReady(() => { Routes.activeMap.setOption(optionName, optionValue) })
    }
  }

  return callbacks
}

export const rightSidebarProps = ({ dispatch, userPrefs, setPreference }) => {
  if (!Routes.New.mapCommand) {
    Routes.New.mapCommand = getSidebarProps({ userPrefs, setPreference })
  }
  const map = Routes.activeMap
  const needsUpgrade = !window.rwgps.config.privileges.advanced_planning
  const actions = {
    promptUpgrade: feature => {
      rwgps.carouselUpgradeModal(_.extend({ feature: `planner${feature.charAt(0).toUpperCase()}${feature.slice(1)}` }, rwgps.carouselUpgradeProps))
    },
    undo: () => map.undo(),
    redo: () => map.redo(),
    clearMap: () => {
      dispatch(plannerReducerActions.setHoveredFeature(null))
      map.clearMapWithHistory()
    },
    reverseRoute: () => map.reverseRoute(),
    outAndBack: () => map.outAndBack(),
    duplicate: () => {
      if (!rwgps.config.privileges['advanced_planning']) {
        return rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerDuplicate' }, rwgps.carouselUpgradeProps))
      }
      const attrs = map.activeRoute.toSaveObj()
      delete attrs.id
      delete attrs.name
      const route = new rwgps.Route(attrs, map)
      map.activeRoute.pois.forEach(poi => { route.pois.push(poi.copy()) })
      map.activeRoute.drawInactive = false
      map.setActiveRoute(route)
      map.snapshot()
    },
    splitRoute: () => {
      if (!rwgps.config.privileges['advanced_planning']) {
        return rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerSplitRoute' }, rwgps.carouselUpgradeProps))
      }

      map.enableRouteSplitter()
      dispatch(plannerReducerActions.setShowRouteSplitter(true))

      const closeRouteSplitter = () => {
        map.disableRouteSplitter()
        dispatch(plannerReducerActions.setShowRouteSplitter(false))
        rwgps.Events.unbind('map:routes_changed', closeRouteSplitter)
      }
      rwgps.Events.once('map:routes_changed', closeRouteSplitter)
    },
    manualTrace: () => {
      if (!rwgps.config.privileges['advanced_planning']) {
        return rwgps.carouselUpgradeModal(_.extend({ feature: 'plannerTrace' }, rwgps.carouselUpgradeProps))
      }
      const route = new rwgps.Route(map.activeRoute.toSaveObj(), map)
      route.connectionPointDisabled = true
      route.routeData.name = '(copy to trace)'
      route.name = '(copy to trace)'
      map.addRoute(route)
      map.activeRoute.removeTrack()
      map.snapshot()
    },
    trace: () => {
      if (needsUpgrade) {
        actions.promptUpgrade('trace')
      } else {
        dispatch(plannerReducerActions.setShowTraceTool(true))
      }
    },
    clearCues: () => {
      map.activeRoute.deleteCoursePoints()
      map.snapshot()
      Routes.Common.populateCoursePoints([])
    },
    clearPoi: () => {
      map.activeRoute.pois = []
      map.loadPois(map.activeRoute.pois, false)
      map.snapshot()
    }
  }
  return { actions, ...Routes.New.mapCommand, userPrefs }
}

export const cueReviewProps = ({ dispatch }) => {
  const map = Routes.activeMap
  const rerenderCueReview = () => {
    dispatch(plannerReducerActions.forceRerenderCueReview)
  }
  const throttledRerenderCueReview = _.throttle(rerenderCueReview, 100)

  return {
    route: map.activeRoute,
    cues: map.activeRoute.coursePoints(),
    onMount: () => {
      Routes.sampleGraph.unbindMouseInteractions()
      map.inCueReview = true

      rwgps.Events.bind('map:routes_changed', throttledRerenderCueReview)
      rwgps.Events.bind('cp:populate', throttledRerenderCueReview)
    },
    closeCueReview: () => {
      Routes.sampleGraph.bindMouseInteractions()
      dispatch(plannerReducerActions.setShowCueReview(false))

      rwgps.Events.unbind('map:routes_changed', throttledRerenderCueReview)
      rwgps.Events.unbind('cp:populate', throttledRerenderCueReview)
    },
    onSave: () => dispatch(plannerReducerActions.setShowSave(true))
  }
}

export const routeSplitterProps = ({ dispatch }) => {
  const map = Routes.activeMap
  const closeRouteSplitter = () => {
    map.disableRouteSplitter()
    dispatch(plannerReducerActions.setShowRouteSplitter(false))
    rwgps.Events.unbind('map:routes_changed', closeRouteSplitter)
  }
  return {
    route: map.activeRoute,
    closeRouteSplitter
  }
}

export const loadExistingModalProps = () => {
  const map = Routes.activeMap
  return {
    loadRoute: (id, type, options) => {
      options = options || {}
      if (!options.ignorePrivileges && !rwgps.config.privileges['advanced_planning']) return
      if (id === null || id === undefined || id === '') return

      const onSuccess = options.onSuccess

      const urlRoot = type === 'trip'
        ? 'trips'
        : 'routes'

      fetchJSON('/' + urlRoot + '/' + id + '.json').then((data) => {
      // need to get rid of these photo id refs otherwise the planner will carry them along to the save form
      // which will result in copying the photos to the saved route
        data.photo_ids = []
        data.points_of_interest.forEach(poi => { poi.photo_ids = null })

        if (type === 'trip') {
          // if type === 'trip', then we need to make sure not to include trippy attributes in the route planner
          data.track_points.forEach(tp => {
            ['t', 's', 'h', 'T', 'c', 'p'].forEach(attr => delete tp[attr])
          })
        }

        const route = new rwgps.Route(data, map, { isTrip: type === 'trip' })
        // only attempt to load the route if it has a non-empty track
        // if it has an empty track, but contains poi, load them onto
        // the existing active route.
        // this allows folks with ACA files (one file with track, one
        // file with POI) to merge their files together
        if (route.trackPoints().length) {
          map.setActiveRoute(route)
          map.snapshot({ doSync: true })
          map.recenterMap()
        } else if (data.points_of_interest.length) {
          if (data.points_of_interest.length > 0) {
            const pois = rwgps.PointOfInterest.fromJSON(data.points_of_interest, false)
            map.activeRoute.pois = map.activeRoute.pois.concat(pois)
            map.loadPois(map.activeRoute.pois)
          }
        }
        if (onSuccess) onSuccess(route)
      })
      return false
    },
    loadFile: ({ filename, parsed }) => {
      let route
      parsed.tracks.forEach(({ pois, course_points: coursePoints, track, name }, i) => {
        route = new rwgps.Route({
          name: name || (`${filename}${parsed.tracks.length > 1 ? ` (${i})` : ''}`),
          course_points: coursePoints,
          points_of_interest: pois,
          track_points: track
        }, map)
        map.addRoute(route)
      })
      if (route) {
        map.setActiveRoute(route)
        map.snapshot({ doSync: true })
        map.recenterMap()
      }
      if (parsed.pois.length > 0) {
        const pois = rwgps.PointOfInterest.fromJSON(parsed.pois, false)
        map.activeRoute.pois = map.activeRoute.pois.concat(pois)
        map.loadPois(map.activeRoute.pois)
      }
    }
  }
}

export const saveRouteProps = ({ dispatch }) => {
  const route = Routes.activeMap.activeRoute.toSaveObj()
  const ps = Routes.activeMap.pointsOfInterestObjs()

  return {
    map: {
      activeRoute: route,
      routes: Routes.activeMap.routes.map(r => r.toSaveObj()),
      plannerOptions: Routes.activeMap.plannerOptions.getOptions()
    },
    ps: ps,
    cleanUpModal: (response) => {
      Routes.activeMap.showElevation()
      dispatch(plannerReducerActions.setShowSave(false))

      if (response.saved) {
        Routes.activeMap.activeRoute.routeData = response.route
        Routes.activeMap.activeRoute.tripData = response.trip
        // update the appropriate planner route with the id the server gave us
        const active = Routes.activeMap.routes.find(r => r.active)
        if (active && response.route) {
          active.mysqlId = response.route.id
          active.name = response.route.name
        }
        // if we've overwritten route(s) in the planner, remove it/them
        const toRemove = Routes.activeMap.routes.filter(r => !r.active && r.mysqlId === active.mysqlId)
        if (toRemove.length) {
          toRemove.forEach(r => Routes.activeMap.removeRoute(r, true))
          Routes.activeMap.snapshot()
          rwgps.Events.trigger('removed')
        }
        rwgps.Events.trigger('map:routes_changed')
      }
    },
    clearPlannerSaved: () => {
      window.onbeforeunload = null
      Routes.activeMap.initializeLocallySavedRoute()
    }
  }
}

export const hotkeys = () => {
  const map = Routes.activeMap
  const graph = Routes.sampleGraph
  const zoom = changeBy => () => {
    const newZoom = map.gmap.getZoom() + changeBy
    // Map#setZoom protects from us setting an invalid zoom, so no need to check here
    map.gmap.setZoom(newZoom)
  }
  let zoomedIn = true
  let zoomInLevel, centerLatLng, mapZoomListener
  const toggleZoom = () => {
    if (zoomedIn) {
      zoomInLevel = map.gmap.getZoom()
      centerLatLng = map.gmap.getCenter()
      zoomedIn = false

      // this is just zoomOut from sample_graph.controls.js
      if (graph.zoomedIn()) {
        graph.zoomOutOnClick(null, false)
      } else {
        graph.recenterMap()
      }

      mapZoomListener = map.gmap.addListener('zoom_changed', () => {
        zoomedIn = true
        mapZoomListener.remove()
      })
    } else {
      map.gmap.setZoom(zoomInLevel)
      map.gmap.setCenter(centerLatLng)
      zoomedIn = true
    }
  }

  return {
    undo: () => !map.inCueReview && map.undo(),
    redo: () => !map.inCueReview && map.redo(),
    clearMap: () => !map.inCueReview && map.clearMapWithConfirm(),
    closeStreetView: () => map.gmap.streetView.setVisible(false),
    addRoute: () => !map.inCueReview && leftSidebarProps({}).startNewRoute(),
    zoomIn: zoom(1),
    zoomOut: zoom(-1),
    toggleZoom
  }
}
